import React from 'react';
import { motion } from 'framer-motion';
import { Check, BookOpen, FileText, PenTool, ClipboardCheck, Calendar, Gamepad2, Star, ShieldCheck, ChevronDown, AlertCircle, Clock, Download } from 'lucide-react';
import { PurchaseNotification } from '../components/PurchaseNotification';
// Product Images
const IMAGES = {
  cover: 'https://cdn.magicpatterns.com/uploads/u8EPSpjFqQfvifE17jnoKk/cover.png',
  bono1: 'https://cdn.magicpatterns.com/uploads/swnPLM8EALQnTcS1GLVGXV/bono_1.png',
  bono2: 'https://cdn.magicpatterns.com/uploads/5Pt77SbVBAVWAQgWsPMXhg/bono_2.png',
  bono3: 'https://cdn.magicpatterns.com/uploads/pqYhRb9P8zRaKUvNMdtx9k/bono_3.png',
  bono4: 'https://cdn.magicpatterns.com/uploads/bqedTohXWAdB6s2E7p4YtY/bono_4.png'
};
const COLORS = {
  red: '#DC2626',
  blue: '#2563EB',
  yellow: '#FBBF24',
  darkBlue: '#1E3A8A',
  bgLight: '#F3F4F6'
};
const TESTIMONIALS = [{
  name: 'M. A.',
  location: 'Argentina',
  role: 'Secundaria',
  text: 'Me ordenó el año. Las consignas están súper claras y no siento que tengo que inventar todo.'
}, {
  name: 'P. R.',
  location: 'Chile',
  role: 'Primaria',
  text: 'Las actividades de comprensión lectora son exactamente lo que necesitaba: listas y bien pensadas.'
}, {
  name: 'L. G.',
  location: 'México',
  role: 'Prof. particular',
  text: 'Lo uso para preparar clases en minutos. Me salvó con alumnos de distintos niveles.'
}, {
  name: 'S. D.',
  location: 'Colombia',
  role: 'Secundaria',
  text: 'Las rúbricas son oro. Corregí mucho más rápido y con criterios claros.'
}, {
  name: 'A. M.',
  location: 'Perú',
  role: 'Primaria',
  text: 'Todo está ordenado y sirve de verdad. No es relleno.'
}];
const FAQS = [{
  q: '¿Es imprimible?',
  a: 'Sí. Está diseñado para imprimir y usar directamente en clase.'
}, {
  q: '¿Viene organizado por unidades/temas?',
  a: 'Sí. Está organizado por carpetas y categorías (leer, escribir, gramática, evaluar).'
}, {
  q: '¿Incluye rúbricas / criterios de corrección?',
  a: 'Sí. Incluye rúbricas y criterios listos para aplicar.'
}, {
  q: '¿Es editable?',
  a: 'Incluye versiones editables cuando corresponde y es fácilmente adaptable.'
}, {
  q: '¿Cómo lo descargo?',
  a: 'Acceso inmediato al finalizar la compra.'
}, {
  q: '¿Y si no me sirve?',
  a: 'Tenés garantía 7 o 14 días (editable). Lo probás y decidís.'
}, {
  q: '¿Trae contenido útil o relleno?',
  a: 'Trae material pensado para usar: textos, actividades, consignas, evaluaciones y rúbricas.'
}];
const MODULES = [{
  title: '1) Banco de Textos + Comprensión Lectora',
  icon: <BookOpen className="w-6 h-6" data-id="element-11" />,
  items: ['Textos narrativos, informativos y argumentativos', 'Preguntas literales, inferenciales y críticas', 'Consignas claras y aplicables', 'Actividades por niveles (adaptables)']
}, {
  title: '2) Guías de Gramática y Ortografía',
  icon: <FileText className="w-6 h-6" data-id="element-12" />,
  items: ['Explicación breve y directa', 'Ejercicios listos para imprimir', 'Refuerzo y repaso']
}, {
  title: '3) Producción Escrita (por tipo textual)',
  icon: <PenTool className="w-6 h-6" data-id="element-13" />,
  items: ['Consignas por tipo de texto (cuento, noticia, carta, etc.)', 'Plantillas para guiar la escritura', 'Checklists para revisar antes de entregar']
}, {
  title: '4) Rúbricas y Criterios de Corrección Listos',
  icon: <ClipboardCheck className="w-6 h-6" data-id="element-14" />,
  items: ['Rúbricas ajustables', 'Criterios claros (contenido, coherencia, ortografía)', 'Formatos rápidos para evaluación']
}, {
  title: '5) Evaluaciones Listas por Unidad / Tema',
  icon: <Check className="w-6 h-6" data-id="element-15" />,
  items: ['Pruebas preparadas con consignas claras', 'Adaptables', 'Pauta/clave orientativa incluida']
}, {
  title: '6) Planificación Anual + Semanales',
  icon: <Calendar className="w-6 h-6" data-id="element-16" />,
  items: ['Estructura anual sugerida', 'Plantillas semanales listas para completar', 'Registro y seguimiento']
}, {
  title: '7) Juegos y Actividades Dinámicas',
  icon: <Gamepad2 className="w-6 h-6" data-id="element-17" />,
  items: ['Dinámicas para lectura y escritura', 'Actividades participativas', 'Recursos para aula y grupos']
}];
const BONUSES = [{
  title: 'BONO 1 – Kit de Evaluaciones Premium',
  desc: 'Evaluaciones listas con criterios y pautas para corregir más rápido.',
  image: IMAGES.bono3,
  value: '50+ pruebas'
}, {
  title: 'BONO 2 – Banco de Juegos de Lectura y Escritura',
  desc: 'Recursos lúdicos para motivar y sostener la participación.',
  image: IMAGES.bono2,
  value: '30+ dinámicas'
}, {
  title: 'BONO 3 – Plantillas Profesionales Docentes',
  desc: 'Calendario, registro, seguimiento, organización anual y planillas.',
  image: IMAGES.bono4,
  value: 'Organización total'
}, {
  title: 'BONO EXTRA – Corrector Express',
  desc: 'Banco de rúbricas + frases de retroalimentación.',
  image: IMAGES.bono1,
  value: 'Feedback rápido'
}];
export function LandingPage() {
  const scrollToPurchase = () => {
    const element = document.getElementById('compra');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-900 overflow-x-hidden" data-id="element-18">
      <PurchaseNotification data-id="element-19" />

      {/* Hero Section */}
      <header className="bg-gradient-to-b from-blue-900 to-blue-800 text-white pt-12 pb-20 px-4 relative overflow-hidden" data-id="element-20">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/notebook.png')]" data-id="element-21"></div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-22">
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} className="text-center md:text-left" data-id="element-23">
            <div className="inline-block bg-yellow-400 text-blue-900 font-bold px-4 py-1 rounded-full text-sm mb-6 shadow-lg transform -rotate-1" data-id="element-24">
              ¡NUEVO 2024!
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" data-id="element-25">
              Lengua y Literatura{' '}
              <span className="text-yellow-400" data-id="element-26">listas para todo el año</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 font-light" data-id="element-27">
              Sin perder horas planificando. Actividades, evaluaciones y
              rúbricas listas para imprimir y usar.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start" data-id="element-28">
              <button onClick={scrollToPurchase} className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-xl shadow-lg transform transition hover:scale-105 hover:shadow-2xl flex items-center justify-center gap-2 text-lg" data-id="element-29">
                <Download className="w-5 h-5" data-id="element-30" />
                Quiero acceder al MegaPack ahora
              </button>
            </div>
            <p className="mt-4 text-sm text-blue-200 flex items-center justify-center md:justify-start gap-2" data-id="element-31">
              <Check className="w-4 h-4 text-green-400" data-id="element-32" /> Acceso inmediato -
              Descarga digital
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.8,
          rotate: 5
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-33">
            <div className="absolute inset-0 bg-white/20 blur-3xl rounded-full transform scale-90" data-id="element-34"></div>
            <img src={IMAGES.cover} alt="MegaPack Lengua y Literatura" className="w-full max-w-md mx-auto drop-shadow-2xl transform hover:scale-105 transition duration-500 cursor-pointer" onClick={scrollToPurchase} data-id="element-35" />
          </motion.div>
        </div>
      </header>

      {/* Benefits Checklist Bar */}
      <section className="bg-yellow-400 py-6 shadow-md relative z-20 -mt-8 mx-4 md:mx-0 rounded-xl md:rounded-none" data-id="element-36">
        <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-x-8 gap-y-4 text-blue-900 font-bold text-sm md:text-base px-4" data-id="element-37">
          <div className="flex items-center gap-2" data-id="element-38">
            <Check className="w-5 h-5 bg-blue-900 text-yellow-400 rounded-full p-1" data-id="element-39" />
            Ahorrá horas de planificación
          </div>
          <div className="flex items-center gap-2" data-id="element-40">
            <Check className="w-5 h-5 bg-blue-900 text-yellow-400 rounded-full p-1" data-id="element-41" />
            Textos + consignas listas
          </div>
          <div className="flex items-center gap-2" data-id="element-42">
            <Check className="w-5 h-5 bg-blue-900 text-yellow-400 rounded-full p-1" data-id="element-43" />
            Evaluaciones con pautas
          </div>
          <div className="flex items-center gap-2" data-id="element-44">
            <Check className="w-5 h-5 bg-blue-900 text-yellow-400 rounded-full p-1" data-id="element-45" />
            Rúbricas listas para corregir
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-46">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="bg-white p-8 md:p-12 rounded-2xl shadow-xl border-t-4 border-red-500" data-id="element-47">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-48">
            Si das Lengua y Literatura, seguro te pasó:
          </h2>
          <p className="text-lg text-gray-700 mb-6" data-id="element-49">
            Te sentás a planificar "un ratito"… y cuando levantás la cabeza ya
            pasó media tarde.
          </p>
          <p className="text-lg text-gray-700 mb-6 font-medium" data-id="element-50">
            No porque seas lenta. No porque no sepas. Sino porque Lengua exige
            demasiado:
          </p>

          <ul className="space-y-4 mb-8" data-id="element-51">
            {['Buscar textos adecuados', 'Crear consignas que realmente funcionen', 'Adaptar niveles (porque no todos están al mismo ritmo)', 'Armar actividades claras (sin que se vuelva relleno)', 'Pensar evaluaciones', 'Y encima… corregir escritura (que consume energía mental real)'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-52">
                <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-53" />
                <span className="text-gray-700" data-id="element-54">{item}</span>
              </li>)}
          </ul>

          <div className="bg-gray-100 p-6 rounded-xl border-l-4 border-gray-400 italic text-gray-700" data-id="element-55">
            "Nadie debería sostener un año entero de Lengua inventando material
            cada semana. Porque mientras planificás, buscás y corregís… la lista
            de pendientes sigue creciendo."
          </div>
        </motion.div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-blue-50 px-4" data-id="element-56">
        <div className="max-w-6xl mx-auto" data-id="element-57">
          <div className="text-center mb-16" data-id="element-58">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-59">
              Imaginate esto:
            </h2>
            <p className="text-xl text-blue-700 max-w-3xl mx-auto" data-id="element-60">
              Abrís una carpeta. Elegís el tema. Imprimís. Y ya tenés la clase
              resuelta.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-61">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-62">
              <img src={IMAGES.cover} alt="Interior del pack" className="w-full rounded-lg shadow-2xl transform rotate-2 hover:rotate-0 transition duration-500" data-id="element-63" />
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-64">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-65">
                MegaPack Lengua y Literatura Lista
              </h3>
              <p className="text-gray-700 mb-6" data-id="element-66">
                Material digital listo para imprimir y usar, creado para
                docentes que necesitan ahorrar tiempo, enseñar con más claridad
                y tener un año organizado desde el día 1.
              </p>
              <p className="text-gray-700 mb-8" data-id="element-67">
                No es "un PDF más". Es un sistema completo para que Lengua deje
                de ser una carga y vuelva a ser una materia que puedas enseñar
                con tranquilidad.
              </p>

              <div className="space-y-4" data-id="element-68">
                <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-69">
                  <div className="bg-green-100 p-2 rounded-full" data-id="element-70">
                    <Check className="w-5 h-5 text-green-600" data-id="element-71" />
                  </div>
                  <span className="font-medium" data-id="element-72">
                    Abrís, elegís tema y la clase sale en minutos
                  </span>
                </div>
                <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-73">
                  <div className="bg-green-100 p-2 rounded-full" data-id="element-74">
                    <Check className="w-5 h-5 text-green-600" data-id="element-75" />
                  </div>
                  <span className="font-medium" data-id="element-76">
                    Consignas pensadas para que el estudiante produzca
                  </span>
                </div>
                <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-77">
                  <div className="bg-green-100 p-2 rounded-full" data-id="element-78">
                    <Check className="w-5 h-5 text-green-600" data-id="element-79" />
                  </div>
                  <span className="font-medium" data-id="element-80">
                    Corregís con rúbricas claras (menos desgaste)
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Included (Modules) */}
      <section className="py-20 px-4 bg-white" data-id="element-81">
        <div className="max-w-6xl mx-auto" data-id="element-82">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16" data-id="element-83">
            Lo que incluye el MegaPack{' '}
            <span className="text-blue-600" data-id="element-84">(lo que realmente usás)</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-85">
            {MODULES.map((module, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-lg transition duration-300 hover:border-blue-200" data-id="element-86">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center text-blue-600 mb-4" data-id="element-87">
                  {module.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 min-h-[56px]" data-id="element-88">
                  {module.title}
                </h3>
                <ul className="space-y-2" data-id="element-89">
                  {module.items.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-90">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" data-id="element-91"></div>
                      {item}
                    </li>)}
                </ul>
              </motion.div>)}
          </div>

          <div className="mt-16 text-center bg-yellow-50 p-8 rounded-2xl border border-yellow-200 max-w-3xl mx-auto" data-id="element-92">
            <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-93">
              "Ok, pero… ¿sirve para mi grado/año?"
            </h3>
            <p className="text-gray-700" data-id="element-94">
              Sí. Este pack está pensado para{' '}
              <span className="font-bold" data-id="element-95">primaria y secundaria</span>, y el
              material es adaptable. Lo importante no es que el documento diga
              "3° A", sino que tengas textos seleccionados, consignas claras y
              estructura didáctica.
            </p>
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white px-4" data-id="element-96">
        <div className="max-w-6xl mx-auto" data-id="element-97">
          <div className="text-center mb-16" data-id="element-98">
            <span className="bg-red-600 text-white font-bold px-4 py-1 rounded-full text-sm uppercase tracking-wide mb-4 inline-block" data-id="element-99">
              Solo por hoy
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-100">
              Bonos incluidos GRATIS
            </h2>
            <p className="text-xl text-gray-300" data-id="element-101">
              Para que el año quede aún más resuelto
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-102">
            {BONUSES.map((bonus, idx) => <motion.div key={idx} whileHover={{
            y: -10
          }} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 flex flex-col" data-id="element-103">
                <div className="relative mb-4 group" data-id="element-104">
                  <div className="absolute top-2 right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded shadow" data-id="element-105">
                    GRATIS
                  </div>
                  <img src={bonus.image} alt={bonus.title} className="w-full rounded-lg shadow-lg transform group-hover:scale-105 transition duration-300" data-id="element-106" />
                </div>
                <h3 className="text-lg font-bold text-yellow-400 mb-2 leading-tight" data-id="element-107">
                  {bonus.title}
                </h3>
                <p className="text-sm text-gray-300 mb-4 flex-grow" data-id="element-108">
                  {bonus.desc}
                </p>
                <div className="bg-white/10 rounded px-3 py-2 text-xs font-medium text-center text-blue-200" data-id="element-109">
                  Incluye: {bonus.value}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="compra" className="py-20 px-4 bg-white relative overflow-hidden" data-id="element-110">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-50" data-id="element-111"></div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-112">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-blue-100" data-id="element-113">
            <div className="bg-blue-600 p-6 text-center text-white" data-id="element-114">
              <h2 className="text-2xl md:text-3xl font-bold" data-id="element-115">
                Oferta Especial Limitada
              </h2>
              <p className="opacity-90" data-id="element-116">
                Todo lo que necesitás para tu año escolar
              </p>
            </div>

            <div className="p-8 md:p-12 text-center" data-id="element-117">
              <div className="flex justify-center items-center gap-4 mb-8" data-id="element-118">
                <div className="text-gray-400 line-through text-2xl font-medium" data-id="element-119">
                  $47.00
                </div>
                <div className="text-6xl font-extrabold text-red-600" data-id="element-120">
                  $13.99
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 text-left mb-10 max-w-2xl mx-auto" data-id="element-121">
                <ul className="space-y-3" data-id="element-122">
                  <li className="flex items-center gap-2" data-id="element-123">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-124" />
                    <span data-id="element-125">MegaPack Completo (7 Módulos)</span>
                  </li>
                  <li className="flex items-center gap-2" data-id="element-126">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-127" />
                    <span data-id="element-128">Acceso Inmediato de por vida</span>
                  </li>
                  <li className="flex items-center gap-2" data-id="element-129">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-130" />
                    <span data-id="element-131">Material 100% Imprimible</span>
                  </li>
                </ul>
                <ul className="space-y-3" data-id="element-132">
                  <li className="flex items-center gap-2" data-id="element-133">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-134" />
                    <span data-id="element-135">BONO: Kit Evaluaciones Premium</span>
                  </li>
                  <li className="flex items-center gap-2" data-id="element-136">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-137" />
                    <span data-id="element-138">BONO: Banco de Juegos</span>
                  </li>
                  <li className="flex items-center gap-2" data-id="element-139">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-140" />
                    <span data-id="element-141">BONO: Plantillas Docentes</span>
                  </li>
                </ul>
              </div>

              <a href="Link de tu checkout" target="_blank" rel="noopener noreferrer" className="block w-full max-w-md mx-auto bg-red-600 hover:bg-red-700 text-white font-bold py-5 px-8 rounded-xl shadow-xl transform transition hover:scale-105 text-xl mb-6" data-id="element-142">
                Sí, quiero acceder al MegaPack con descuento
              </a>

              <div className="flex items-center justify-center gap-4 text-sm text-gray-500" data-id="element-143">
                <span className="flex items-center gap-1" data-id="element-144">
                  <ShieldCheck className="w-4 h-4" data-id="element-145" /> Compra segura
                </span>
                <span className="flex items-center gap-1" data-id="element-146">
                  <Download className="w-4 h-4" data-id="element-147" /> Entrega digital
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50 px-4" data-id="element-148">
        <div className="max-w-6xl mx-auto" data-id="element-149">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-150">
            Docentes que ya lo usan
          </h2>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-151">
            {TESTIMONIALS.map((t, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className={`bg-white p-6 rounded-xl shadow-md border border-gray-100 ${idx >= 3 ? 'md:col-span-1.5' : ''}`} data-id="element-152">
                <div className="flex items-center gap-1 text-yellow-400 mb-4" data-id="element-153">
                  {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 fill-current" data-id="element-154" />)}
                </div>
                <p className="text-gray-700 mb-6 italic" data-id="element-155">"{t.text}"</p>
                <div className="flex items-center gap-3" data-id="element-156">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm" data-id="element-157">
                    {t.name.substring(0, 2)}
                  </div>
                  <div data-id="element-158">
                    <p className="font-bold text-gray-900 text-sm" data-id="element-159">{t.name}</p>
                    <p className="text-xs text-gray-500" data-id="element-160">
                      {t.role} • {t.location}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-16 px-4 bg-white" data-id="element-161">
        <div className="max-w-3xl mx-auto text-center" data-id="element-162">
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-163">
            <ShieldCheck className="w-10 h-10 text-yellow-600" data-id="element-164" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-165">
            Garantía de Satisfacción (Comprás sin riesgo)
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-166">
            Queremos que lo compres con tranquilidad. Por eso tenés Garantía de
            7 días: si lo descargás y sentís que no te sirve para tu realidad
            docente, pedís el reembolso y listo. Sin vueltas.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50 px-4" data-id="element-167">
        <div className="max-w-3xl mx-auto" data-id="element-168">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-169">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-4" data-id="element-170">
            {FAQS.map((faq, idx) => <div key={idx} className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-171">
                <details className="group" data-id="element-172">
                  <summary className="flex justify-between items-center font-medium cursor-pointer list-none p-6" data-id="element-173">
                    <span className="text-lg text-gray-900" data-id="element-174">{faq.q}</span>
                    <span className="transition group-open:rotate-180" data-id="element-175">
                      <ChevronDown className="w-5 h-5 text-gray-500" data-id="element-176" />
                    </span>
                  </summary>
                  <div className="text-gray-600 px-6 pb-6 pt-0" data-id="element-177">{faq.a}</div>
                </details>
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-blue-900 text-white px-4 text-center" data-id="element-178">
        <div className="max-w-4xl mx-auto" data-id="element-179">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-180">
            Planificar Lengua no debería sentirse como cargar una mochila extra
          </h2>
          <p className="text-xl text-blue-200 mb-10 max-w-2xl mx-auto" data-id="element-181">
            Este MegaPack te promete algo real: orden, claridad y horas
            recuperadas. Para que puedas enseñar mejor y vivir el año con más
            tranquilidad.
          </p>
          <button onClick={scrollToPurchase} className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-5 px-10 rounded-xl shadow-lg transform transition hover:scale-105 text-xl flex items-center justify-center gap-3 mx-auto" data-id="element-182">
            Sí, quiero el MegaPack Lengua y Literatura Lista
            <Check className="w-6 h-6" data-id="element-183" />
          </button>
          <p className="mt-6 text-sm text-blue-300" data-id="element-184">
            Acceso inmediato + Bonos incluidos hoy
          </p>
        </div>
      </section>

      <footer className="bg-gray-900 text-gray-500 py-8 text-center text-sm" data-id="element-185">
        <p data-id="element-186">© 2024 MegaPack Docente. Todos los derechos reservados.</p>
        <p className="mt-2" data-id="element-187">
          Este sitio no forma parte de Facebook o Facebook Inc.
        </p>
      </footer>
    </div>;
}